import ApiService from './api.service';
import { FileDataInterface } from '@/store/interfaces/filedata.interface';
import { store } from '@/store/store';
import { McsisRole } from '@/models/constants/McsisRole';
import { isInRole as isInRoleLocal } from '@/plugins/auth';
import { InclusionTypes, TemplateTypes } from '@/models/VariousEnum';
import BlobConverter from '@/utils/blobconverter';
import { isPharmacistFitOrderActivated } from '@/config/app.config';

export default class DocumentService {
  public static get endpoint(): string {
    return process.env.VUE_APP_PUBLIC_ROOT_API;
  }

  /**
   * Get file object
   *
   * @returns {IFileData} a file object
   * @throws ApiError
   */
  public static async getDocumentFromBackend(partId: number,
                                             language: string, type: string,
                                             handlerSuccessEnabled: boolean = false): Promise<FileDataInterface> {

    const url = `${this.endpoint}/Participants/${partId}/documents/document`;

    return await ApiService.getFileFromBackend(url, { language, type }, `document_${type}`, handlerSuccessEnabled)
      .then((response: FileDataInterface) => {
        return response;
      });
  }

  /**
   * Prepare the PDF file for the user (participant or pro user)
   * @param partId The participant ID
   * @param locale The current i18n locale
   * @param isRefusalOrExclusion True in case it's a refusal or an exlcusion
   */
  public static async preparePDF(
    partId: number,
    locale: string,
    isRefusalOrExclusion: boolean,
    route: string): Promise<void> {
    const isParticipant = await isInRoleLocal(McsisRole.Participant).then((isInRole: boolean) => isInRole );
    const isPharmacist = await isInRoleLocal(McsisRole.Pharmacist).then((isInRole: boolean) => isInRole );
    const isDoctor = await isInRoleLocal(McsisRole.Doctor).then((isInRole: boolean) => isInRole );
    const pharmacistOrderFitActivated = isPharmacistFitOrderActivated(route);

    if (isParticipant) {
      if (store.state.isColonoscopyMode) {
        // Colonoscopy mode: in this case we must download prepare a confirmation PDF (if any configured)
        if (!isRefusalOrExclusion) {
          if (store.state.inclusionType === InclusionTypes.fit) {
            // prepare confirmation document for FIT
            await this.getTemplateAndPreparePDF(partId, locale, TemplateTypes.ConfirmationFIT);
          }
          if (store.state.inclusionType === InclusionTypes.colo) {
            // prepare confirmation document for colo
            await this.getTemplateAndPreparePDF(partId, locale, TemplateTypes.ConfirmationColo);
          }
        } else {
          // prepare confirmation document for non-participation
          await this.getTemplateAndPreparePDF(partId, locale, TemplateTypes.ConfirmationNonParticipation);
        }

      } else {
        // Classic mode: just send to the queue and redirect to confirmation page
        // => no template to prepare for this case.
      }
    } else if (pharmacistOrderFitActivated && isPharmacist) {
      await this.getTemplateAndPreparePDF(partId, locale, TemplateTypes.ConfirmationFIT);
    } else {
      const followUpType = isDoctor ? TemplateTypes.FollowUpColo : TemplateTypes.FollowUpFIT;

      // Doctor: Only print follow up colo, they are ordering a FIT and they don't need a follow up FIT
      // Pharmacist: Only print follow up FIT because they give the FIT directly
      await this.getTemplateAndPreparePDF(partId, locale, followUpType);
    }
  }

  /**
   * Get the template by its type, and if it exists save it into the localStorage
   * @param partId The participant ID
   * @param locale The current i18n locale
   * @param templateType The template type
   */
  private static async getTemplateAndPreparePDF(partId: number, locale: string, templateType: string): Promise<void> {
    try {
      const res = await this.getDocumentFromBackend(partId, locale, templateType);
      BlobConverter.saveBlobToLocalStorage(res);
    } catch {
      // @ts-ignore
      log(`No template: ${templateType}`);
    }
  }
}
